import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PageLayout, ActionButton } from "../../../components/modern";
import { useAuth } from "../../../contexts/AuthContext";
import { adminApi } from "../../../utils/api";
import { getPasswordValidationErrorKey } from "../../../utils/passwordValidation";
import { showSuccessToast, showErrorToast } from "../../../utils/toastNotifications";

export const CreateProfile: React.FC = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { t, i18n } = useTranslation('customers');
  const isRTL = i18n.language === 'ar';

  const [formData, setFormData] = useState({
    name: "",
    cityId: null,
    countryId: null,
    phone: "",
    password: "",
    email: "",
    address: "",
    address2: "",
    language: "",
  });
  const [cities, setCities] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [countries, setCountries] = useState<any[]>([]);
  const [selectedPhoneCode, setSelectedPhoneCode] = useState<string>("");
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await adminApi.call('/country', 'GET');

        if (!response.success) {
          showErrorToast(response.error || t('messages.fetchError'));
          return;
        }

        setCountries(
          response.data?.items.filter((x: any) => (x.countryType == 1 || x.countryType == 0) && !x.isSuspended) || []
        );
      } catch (error: any) {
        showErrorToast(t('messages.fetchError') + ": " + error.message);
      }
    };

    fetchCountries();
  }, []);
  // Phone validation regex: starts with 7 and exactly 10 digits
  // const phoneValidationRegex = /^7[0-9]{9}$/;

  // Handle input change
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = event.target;

    // Validation for name field (letters, spaces, and common name characters only)
    if (name === 'name') {
      const nameRegex = /^[a-zA-Z\u0600-\u06FF\s'-]*$/; // English, Arabic letters, spaces, apostrophes, hyphens
      if (!nameRegex.test(value)) {
        return; // Don't update if invalid characters
      }
    }

    // Validation for phone field (numbers only, max 10 digits)
    if (name === 'phone') {
      const phoneRegex = /^[0-9]*$/;
      if (!phoneRegex.test(value) || value.length > 10) {
        return; // Don't update if invalid characters or exceeds 10 digits
      }
    }

    if (name == "countryId") {
      fetchCities(value);
      // Update phone code based on selected country
      const selectedCountry = countries.find((c: any) => c.id === Number(value));
      setSelectedPhoneCode(selectedCountry?.phoneCode || "");
    }
    setFormData((prevData) => ({ ...prevData, [name]: name.includes('Id') ? (value ? Number(value) : null) : value }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.name.trim()) {
      newErrors.name = t('customers:validation.nameRequired');
    } else if (formData.name.length < 2) {
      newErrors.name = t('customers:validation.nameMinLength');
    }

    // Email is optional, but if provided, validate format
    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('customers:validation.emailInvalid');
    }

    // Phone is required and must match format (starts with 7, exactly 10 digits)
    if (!formData.phone.trim()) {
      newErrors.phone = t('customers:validation.phoneRequired');
    } 
    // else if (!phoneValidationRegex.test(formData.phone)) {
    //   newErrors.phone = t('customers:validation.phoneInvalid');
    // }

    if (!formData.password.trim()) {
      newErrors.password = t('customers:validation.passwordRequired');
    } else {
      const passwordValidationErrorKey = getPasswordValidationErrorKey(formData.password, formData.phone);
      if (passwordValidationErrorKey) {
        newErrors.password = t(`customers:validation.${passwordValidationErrorKey}`);
      }
    }

    if (!formData.countryId) {
      newErrors.countryId = t('customers:validation.countryRequired');
    }

    if (!formData.cityId) {
      newErrors.cityId = t('customers:validation.cityRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const fetchCities = async (countryId: string) => {
    try {
      const response = await adminApi.call(`/city/listofcityincountry?countryId=${countryId}`, 'GET');

      if (!response.success) {
        showErrorToast(response.error || t('messages.fetchError'));
        return;
      }

      setCities(response.data?.items || []);
    } catch (error: any) {
      showErrorToast(t('messages.fetchError') + ": " + error.message);
    }
  };
  // Submit updated order
  const createProfile = async () => {
    // Validate form before submission
    if (!validateForm()) {
      showErrorToast(t('customers:validation.fillAllFields'));
      return;
    }

    setUpdating(true);
    setErrors({});

    try {
      const response = await adminApi.call('/customer/create', 'POST', formData);

      if (!response.success) {
        showErrorToast(response.error || t('messages.createError'));
        return;
      }

      setFormData({
        name: "",
        cityId: null,
        countryId: null,
        phone: "",
        password: "",
        email: "",
        address: "",
        address2: "",
        language: ""
      });
      showSuccessToast(t('messages.createSuccess'));
      // setTimeout(() => navigate("/customers"), 2000); // Redirect after success
    } catch (error: any) {
      showErrorToast(t('messages.createError') + ": " + error.message);
    } finally {
      setUpdating(false);
    }
  };


  const breadcrumbs = [
    { label: t('breadcrumbs.customers'), path: '/customers' },
    { label: t('breadcrumbs.create') }
  ];

  return (
    <PageLayout
      title={t('createCustomer')}
      subtitle={t('subtitle')}
      breadcrumbs={breadcrumbs}

    >
      <div className="card shadow-lg border-0 rounded-3 p-4">
        <div className="row g-3">
          <div className="col-md-6">
            <label className={`form-label ${isRTL ? 'text-end' : 'text-start'}`}>
              {t('fields.name')} <span className="required">*</span>
            </label>
            <input
              type="text"
              className={`form-control ${isRTL ? 'text-end' : 'text-start'} ${errors.name ? 'is-invalid' : ''}`}
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder={t('placeholders.name')}
              dir={isRTL ? 'rtl' : 'ltr'}
              required
            />
            {errors.name && <div className="invalid-feedback">{errors.name}</div>}
          </div>
          <div className="col-md-6">
            <label className={`form-label ${isRTL ? 'text-end' : 'text-start'}`}>
              {t('fields.email')} <span className="text-muted" style={{ fontSize: '0.8em' }}>({isRTL ? 'اختياري' : 'Optional'})</span>
            </label>
            <input
              type="email"
              className={`form-control ${isRTL ? 'text-end' : 'text-start'} ${errors.email ? 'is-invalid' : ''}`}
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder={t('placeholders.email')}
              dir={isRTL ? 'rtl' : 'ltr'}
            />
            {errors.email && <div className="invalid-feedback">{errors.email}</div>}
          </div>
          <div className="col-md-6">
            <label className={`form-label ${isRTL ? 'text-end' : 'text-start'}`}>
              {t('fields.phone')} <span className="required">*</span>
            </label>
            <div className="input-group">
              <span className="input-group-text" style={{ backgroundColor: '#e9ecef', fontWeight: '500', minWidth: '70px', justifyContent: 'center' }}>
                {selectedPhoneCode || '+---'}
              </span>
              <input
                type="text"
                className={`form-control ${isRTL ? 'text-end' : 'text-start'} ${errors.phone ? 'is-invalid' : ''}`}
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder={isRTL ? '7xxxxxxxxx' : '7xxxxxxxxx'}
                dir="ltr"
                maxLength={10}
                required
              />
              {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
            </div>
            {/* <small className="text-muted" style={{ fontSize: '0.75em' }}>
              {isRTL ? 'يجب أن يبدأ بـ 7 ويتكون من 10 أرقام' : 'Must start with 7 and be exactly 10 digits'}
            </small> */}
          </div>

          <div className="col-md-6">
            <label className={`form-label ${isRTL ? 'text-end' : 'text-start'}`}>
              {t('fields.password')} <span className="required">*</span>
            </label>
            <input
              type="password"
              className={`form-control ${isRTL ? 'text-end' : 'text-start'}`}
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              placeholder={t('placeholders.password')}
              dir={isRTL ? 'rtl' : 'ltr'}
              required
            />
          </div>
          <div className="col-md-6">
            <label className={`form-label ${isRTL ? 'text-end' : 'text-start'}`}>
              {t('fields.country')} <span className="required">*</span>
            </label>
            <select
              className={`form-select ${isRTL ? 'text-end' : 'text-start'}`}
              name="countryId"
              value={formData.countryId || ""}
              onChange={handleInputChange}
              dir={isRTL ? 'rtl' : 'ltr'}
              required
            >
              <option value="">{t('placeholders.country')}</option>
              {countries.map((country) => (
                <option key={country.id} value={country.id}>
                  {country.name}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-6">
            <label className={`form-label ${isRTL ? 'text-end' : 'text-start'}`}>
              {t('fields.city')} <span className="required">*</span>
            </label>
            <select
              className={`form-select ${isRTL ? 'text-end' : 'text-start'}`}
              name="cityId"
              value={formData.cityId || ""}
              onChange={(e) => handleInputChange(e)}
              dir={isRTL ? 'rtl' : 'ltr'}
              required
            >
              <option value="">{t('placeholders.city')}</option>
              {cities.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-6">
            <label className={`form-label ${isRTL ? 'text-end' : 'text-start'}`}>
              {t('fields.language', 'Language')}
            </label>
            <select
              className={`form-select ${isRTL ? 'text-end' : 'text-start'}`}
              name="language"
              value={formData.language || ""}
              onChange={handleInputChange}
              dir={isRTL ? 'rtl' : 'ltr'}
            >
              <option value="">{t('placeholders.language', 'Select Language')}</option>
              <option value="en">{t('common.english', 'English')}</option>
              <option value="ar">{t('common.arabic', 'Arabic')}</option>
            </select>
          </div>

          <div className="col-md-6">
            <label className={`form-label ${isRTL ? 'text-end' : 'text-start'}`}>
              {t('fields.address')}
            </label>
            <input
              type="text"
              className={`form-control ${isRTL ? 'text-end' : 'text-start'}`}
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              placeholder={t('placeholders.address')}
              dir={isRTL ? 'rtl' : 'ltr'}
            />
          </div>
          <div className="col-md-6">
            <label className={`form-label ${isRTL ? 'text-end' : 'text-start'}`}>
              {t('fields.address2')}
            </label>
            <input
              type="text"
              className={`form-control ${isRTL ? 'text-end' : 'text-start'}`}
              name="address2"
              value={formData.address2}
              onChange={handleInputChange}
              placeholder={t('placeholders.address2')}
              dir={isRTL ? 'rtl' : 'ltr'}
            />
          </div>
        </div>

        <div className="text-center mt-4">
          <ActionButton
            variant="secondary"
            onClick={() => navigate('/customers')}
            className={isRTL ? 'ms-2' : 'me-2'}
          >
            <i className={`fa ${isRTL ? 'fa-arrow-right' : 'fa-arrow-left'} ${isRTL ? 'ms-1' : 'me-1'}`}></i>
            {t('actions.back')}
          </ActionButton>
          <ActionButton
            variant="primary"
            onClick={createProfile}
            loading={updating}
            disabled={updating}
          >
            {updating ? t('actions.creating') : t('actions.create')}
          </ActionButton>
        </div>
      </div>
    </PageLayout>
  );
};
